import React, { useRef } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';

import { useAppContext } from '../../../../common/ui/app-context';
import { SearchLinkSkeletons } from '../../../../common/ui/search-link-skeletons';
import { useFetchAll } from '../../../../controllers/dropdown';
import { useInputQuery } from '../../../../controllers/store';
import { useComponents, useFeatures } from '../../../../controllers/store/bootstrap';
import { useDropdownLoading, useResultCount } from '../../../../controllers/store/dropdown';

import { AutoSuggestion } from './auto-suggestion';
import { MimicQuery } from './mimic-query';
import { NoResultsDropdown } from './no-results-dropdown';
import { PeopleAndTeamsResults } from './people-and-teams';
import { QuickSearchResults } from './quick-search';
import { RecentSearchResults } from './recent-searches';
import { RecentViews } from './recent-views';

const searchDropdownStyles = xcss({
	padding: 'space.100',
	gap: 'space.025',
	boxShadow: 'elevation.shadow.overlay',
	borderWidth: '0',
	boxSizing: 'border-box',
});

export type SearchDropdownProps = {
	inputWidth: number;
};

export const SearchDropdown = ({ inputWidth }: SearchDropdownProps) => {
	useFetchAll();
	const [dropdownLoading] = useDropdownLoading();
	const [features] = useFeatures();
	const [components] = useComponents();
	const [query] = useInputQuery();
	const { generateSearchUrl, queryParams } = useAppContext();
	const [resultCount] = useResultCount();
	const totalResultCount = Object.values(resultCount).reduce((sum, count) => sum + count, 0);
	const containerRef = useRef<HTMLObjectElement>(null);

	let footerComponent = null;
	if (components?.footer !== undefined) {
		if (typeof components.footer === 'function') {
			footerComponent = components.footer(
				generateSearchUrl({
					...queryParams,
					text: query,
				}),
			);
		} else if (React.isValidElement(components.footer)) {
			footerComponent = components.footer;
		}
	}

	return (
		<Box
			xcss={searchDropdownStyles}
			style={{
				width: `${inputWidth}px`,
			}}
			aria-label={'Search Suggestions Dropdown'}
			testId={'search-suggestions-dropdown'}
			ref={containerRef}
		>
			{dropdownLoading && (
				<>
					{Boolean(features?.inlineSearch?.enabled) && <MimicQuery />}
					<SearchLinkSkeletons />
				</>
			)}

			{!dropdownLoading && totalResultCount === 0 && <NoResultsDropdown />}

			{!dropdownLoading && totalResultCount > 0 && (
				<>
					{Boolean(features?.inlineSearch?.enabled) && <MimicQuery />}
					{Boolean(features?.autoSuggestion?.enabled) && <AutoSuggestion />}
					{Boolean(features?.recentSearches?.enabled) && <RecentSearchResults />}
					{Boolean(features?.peopleAndTeamsSearch?.enabled) && <PeopleAndTeamsResults />}
					{Boolean(features?.recentViews?.enabled) && <RecentViews />}
					{Boolean(features?.quickSearch?.enabled) && <QuickSearchResults />}
					{footerComponent}
				</>
			)}
		</Box>
	);
};
